import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import { SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import { SortableTh, Table, TableContent, Td, Th } from '@/components/Table';
import { BPTestResource, SortState } from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';

import TestsModal from './TestsModal';
import TestsTableRow from './TestsTableRow';

const Wrap = styled.div`
  overflow: hidden;
`;

const StyledTable = styled(Table)<{ $areThumbsEnlarged: boolean }>`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    font-weight: 700;
    vertical-align: top;
    &:first-child {
      width: 130px;
      min-width: 130px !important;
      padding-left: 24px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
      ${({ $areThumbsEnlarged, theme }) =>
        $areThumbsEnlarged &&
        css`
          width: 312px;
          min-width: 312px !important;
        `}
    }
    &:nth-child(2) {
      width: 200px;
      min-width: 200px !important;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 130px;
      top: 0;
      bottom: 0;
      ${({ $areThumbsEnlarged, theme }) =>
        $areThumbsEnlarged &&
        css`
          left: 312px;
        `}
    }
    &:not(:last-child) {
      min-width: 120px;
    }
    &:last-child {
      min-width: 56px;
      width: 56px;
      z-index: 1;
      position: relative;
      vertical-align: middle;
    }
  }
`;

const ButtonWrapper = styled.div`
  justify-content: flex-end;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  > button {
    padding-left: 16px;
    padding-right: 16px;
    font-weight: 600;
  }
`;

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: 'created_at',
};

interface Props {
  projectId: number;
  projectName: string;
}

const TestsTable: FC<Props> = ({ projectId, projectName }) => {
  const [areThumbsEnlarged, setAreThumbsEnlarged] = useState(false);

  const [showModal, setShowModal] = useState(false);

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const {
    data: tests,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['bp_tests', { projectId, sort }],
    queryFn: () =>
      api.get<never, BPTestResource[]>('/v1/editorial/bp-tests', {
        params: {
          'filter[bp_testing_project_id]': projectId,
          ...(sort.direction !== 'DEFAULT' && {
            sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
          }),
        },
      }),
    staleTime: 90 * 1000,
  });

  return (
    <Wrap>
      <ScrollableTable>
        <StyledTable $areThumbsEnlarged={areThumbsEnlarged}>
          <thead>
            <tr>
              <Th>Thumbnail</Th>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="name"
                title="Name"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="message"
                title="Message"
              />
              <Th>UUID</Th>
              <Th>Link</Th>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="status"
                title="Status"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="effective_status"
                title="Facebook Status"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="channel_name"
                title="Facebook Page"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="user_name"
                title="Creator"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="budget"
                title="Budget"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="stats_updated_at"
                title="Stats Updated At"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="test_duration"
                title="Test Duration"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="impressions"
                title="Impressions"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="spendings"
                title="Spendings"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="clicks"
                title="Clicks"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="link_clicks"
                title="Link Clicks"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="ctr"
                title="CTR"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="reactions"
                title="Reactions"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="comments"
                title="Comments"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="page_likes"
                title="Page Likes"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="currency"
                title="Currency"
              />
              <Th>Clicks Threshold</Th>
              <Th>Target Country</Th>
              <Th>Target Device</Th>
              <Th>Bid Amount</Th>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="created_at"
                title="Created At"
              />
              <Td $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Td>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!tests?.length}
            >
              {tests?.map((test) => (
                <TestsTableRow
                  key={test.id}
                  projectId={projectId}
                  test={test}
                  areThumbsEnlarged={areThumbsEnlarged}
                  setAreThumbsEnlarged={setAreThumbsEnlarged}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <ButtonWrapper>
        <Button onClick={() => setShowModal(true)} $type="colorized">
          Upload tests
        </Button>
      </ButtonWrapper>
      <AnimatePresence>
        {showModal && (
          <TestsModal
            onClose={() => setShowModal(false)}
            projectName={projectName}
            projectId={projectId}
          />
        )}
      </AnimatePresence>
    </Wrap>
  );
};

export default TestsTable;
